import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useExercises, useMuscleGroups } from '../hooks/useExercises';
import CreateExerciseModal from '../components/CreateExerciseModal';
import ViewExerciseModal from '../components/ViewExerciseModal';
import EditExerciseModal from '../components/EditExerciseModal';
import Navigation from '../components/Navigation';
import Pagination from '../components/Pagination';
import SearchInput from '../components/SearchInput';
import { ExerciseCreate, Exercise, ExerciseUpdate, ExerciseFilters } from '../types';
import { Plus, Edit, Trash2, Eye, Filter } from 'lucide-react';

const ExercisesPage: React.FC = React.memo(() => {
  const { trainer, logout } = useAuth();
  
  // Состояние пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMuscleGroup, setSelectedMuscleGroup] = useState<number | ''>('');
  const [onlyMine, setOnlyMine] = useState(false);
  
  const itemsPerPage = 12; // Количество упражнений на странице
  
  // Создаем фильтры для API
  const filters: ExerciseFilters = useMemo(() => {
    const apiFilters: ExerciseFilters = {
      skip: (currentPage - 1) * itemsPerPage,
      limit: itemsPerPage,
    };
    
    if (searchTerm) {
      apiFilters.search = searchTerm;
    }
    
    if (selectedMuscleGroup) {
      apiFilters.muscle_group_id = selectedMuscleGroup;
    }
    
    if (onlyMine) {
      apiFilters.only_mine = true;
    }
    
    return apiFilters;
  }, [currentPage, searchTerm, selectedMuscleGroup, onlyMine, itemsPerPage]);
  
  const { 
    exercises, 
    totalCount,
    isLoading, 
    createExercise, 
    updateExercise,
    deleteExercise, 
    isCreateLoading,
    isUpdateLoading,
    isDeleteLoading 
  } = useExercises(filters);
  
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);

  // Вычисляем общее количество страниц
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Обработчик изменения страницы
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Прокручиваем наверх страницы при смене страницы
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Стабилизированный обработчик для поиска с useRef
  const searchChangeRef = useRef<((value: string) => void) | null>(null);
  const [searchKey, setSearchKey] = useState(0);
  
  if (!searchChangeRef.current) {
    searchChangeRef.current = (value: string) => {
      setSearchTerm(value);
      // Обновляем ключ для пересоздания компонента
      setSearchKey(prev => prev + 1);
    };
  }

  // Сброс на первую страницу при изменении фильтров
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedMuscleGroup, onlyMine]);

  // Получаем группы мышц для фильтра (используем отдельный хук)
  const { muscleGroups } = useMuscleGroups();

  const handleCreateExercise = (data: ExerciseCreate) => {
    createExercise(data);
    setIsCreateModalOpen(false);
  };

  const handleViewExercise = (exercise: Exercise) => {
    setSelectedExercise(exercise);
    setIsViewModalOpen(true);
  };

  const handleEditExercise = (exercise: Exercise) => {
    setSelectedExercise(exercise);
    setIsEditModalOpen(true);
  };

  const handleUpdateExercise = (id: number, data: ExerciseUpdate) => {
    updateExercise({ id, data });
    setIsEditModalOpen(false);
    setSelectedExercise(null);
  };

  const handleDeleteExercise = (exerciseId: number) => {
    if (window.confirm('Вы уверены, что хотите удалить это упражнение?')) {
      deleteExercise(exerciseId);
    }
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedExercise(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedExercise(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Навигационная панель */}
      <Navigation currentPage="exercises" />

      {/* Заголовок страницы */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-6 space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Упражнения</h1>
              <p className="mt-1 text-sm text-gray-500">
                Управление вашими упражнениями
              </p>
            </div>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto"
            >
              <Plus className="mr-2 h-4 w-4" />
              Создать упражнение
            </button>
          </div>
        </div>
      </div>

      {/* Фильтры и поиск */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Поиск */}
            <SearchInput
              key={searchKey}
              value={searchTerm}
              onChange={searchChangeRef.current!}
              placeholder="Поиск упражнений..."
            />

            {/* Фильтр по группе мышц */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <select
                value={selectedMuscleGroup}
                onChange={(e) => setSelectedMuscleGroup(e.target.value ? Number(e.target.value) : '')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Все группы мышц</option>
                {muscleGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Фильтр "Только мои" */}
            <div className="flex items-center">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={onlyMine}
                  onChange={(e) => setOnlyMine(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Только мои</span>
              </label>
            </div>

            {/* Статистика */}
            <div className="text-sm text-gray-500 flex items-center justify-end">
              {searchTerm ? (
                `Показано ${exercises.length} из ${totalCount} результатов поиска`
              ) : (
                `Показано ${exercises.length} из ${totalCount} упражнений`
              )}
            </div>
          </div>
        </div>

        {/* Пагинация сверху */}
        {totalPages > 1 && (
          <div className="mb-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
            />
          </div>
        )}

        {/* Список упражнений */}
        {exercises.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="text-gray-400 mb-4">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                <Plus className="h-8 w-8" />
              </div>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Упражнения не найдены
            </h3>
            <p className="text-gray-500 mb-6">
              {totalCount === 0 
                ? 'У вас пока нет упражнений. Создайте первое упражнение!'
                : 'Попробуйте изменить параметры поиска или фильтры.'
              }
            </p>
            {totalCount === 0 && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="mr-2 h-4 w-4" />
                Создать первое упражнение
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {exercises.map((exercise: Exercise) => (
              <div key={exercise.id} className="bg-white rounded-lg shadow overflow-hidden">
                {/* Изображение упражнения */}
                {exercise.image_url && (
                  <div className="aspect-w-16 aspect-h-9 bg-gray-200">
                    <img
                      src={exercise.image_url}
                      alt={exercise.name}
                      className="w-full h-48 object-cover"
                    />
                  </div>
                )}
                
                {/* Содержимое карточки */}
                <div className="p-6">
                  {/* Заголовок и группы мышц */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {exercise.name}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {exercise.muscle_groups?.map((group) => (
                          <span
                            key={group.id}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {group.name}
                          </span>
                        ))}
                        {exercise.is_public && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            🌍 Публичное
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Описание */}
                  {exercise.description && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {exercise.description}
                    </p>
                  )}

                  {/* Инструкции (сокращенные) */}
                  {exercise.instructions && (
                    <div className="mb-4">
                      <p className="text-xs text-gray-500 mb-1">Инструкции:</p>
                      <p className="text-sm text-gray-700 line-clamp-3">
                        {exercise.instructions}
                      </p>
                    </div>
                  )}

                  {/* Медиа ссылки */}
                  {(exercise.image_url || exercise.video_url) && (
                    <div className="mb-4">
                      <p className="text-xs text-gray-500 mb-2">Медиа:</p>
                      <div className="flex space-x-2">
                        {exercise.image_url && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-100 text-green-800">
                            📷 Изображение
                          </span>
                        )}
                        {exercise.video_url && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-red-100 text-red-800">
                            🎥 Видео
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Действия */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewExercise(exercise)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Просмотр"
                      >
                        <Eye size={18} />
                      </button>
                      {exercise.created_by_trainer_id === trainer?.id && (
                        <button
                          onClick={() => handleEditExercise(exercise)}
                          className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                          title="Редактировать"
                        >
                          <Edit size={18} />
                        </button>
                      )}
                    </div>
                    {exercise.created_by_trainer_id === trainer?.id && (
                      <button
                        onClick={() => handleDeleteExercise(exercise.id)}
                        disabled={isDeleteLoading}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                        title="Удалить"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Пагинация */}
      {totalPages > 1 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalItems={totalCount}
            itemsPerPage={itemsPerPage}
          />
        </div>
      )}

      {/* Модальные окна */}
      <CreateExerciseModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateExercise}
        isLoading={isCreateLoading}
      />
      
      <ViewExerciseModal
        isOpen={isViewModalOpen}
        onClose={handleCloseViewModal}
        exercise={selectedExercise}
      />
      
      <EditExerciseModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSubmit={handleUpdateExercise}
        exercise={selectedExercise}
        isLoading={isUpdateLoading}
      />
    </div>
  );
});

ExercisesPage.displayName = 'ExercisesPage';

export default ExercisesPage; 